import { useCallback, useEffect, useState } from "react";
import { Loader2, GitCompare, History, Link2, Camera, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import Segmented from "@/components/Segmented";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";
import FrsTable from "@/fieldops/components/FrsTable";
import { RoleProvider } from "@/fieldops/context/RoleContext";
import FrsDiff from "./FrsDiff";
import FrsRevisions from "./FrsRevisions";
import FrsUnmatched from "./FrsUnmatched";

export const shortLabel = (it) =>
  it.product_short || [it.product_code, it.product_description].filter(Boolean).join(" ") || "—";

/** Operations view of the ONE digital FRS — the RA table in office mode, plus the scope-control tools. */
export default function FrsTab({ project }) {
  const [overview, setOverview] = useState(null);
  const [tool, setTool] = useState("");
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    try { setOverview(await opsApi.frs(project.id, "current")); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load the FRS"); }
  }, [project.id]);
  useEffect(() => { load(); }, [load, tick]);
  const changed = () => setTick((t) => t + 1);

  const snapshot = async () => {
    const notes = window.prompt("Name this version (optional)") ?? null;
    if (notes === null) return;
    setBusy(true);
    try { const v = await opsApi.frsSnapshot(project.id, notes); toast.success(`Version v${v.version_no} saved`); changed(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save a version"); }
    finally { setBusy(false); }
  };
  const clearReview = async () => {
    try { await opsApi.frsClearReview(project.id, "Reviewed from the FRS tab"); toast.success("Review cleared"); changed(); }
    catch (e) { toast.error("Could not clear the review flag"); }
  };

  if (!overview) {
    return <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading FRS…</div>;
  }
  const c = overview.counts;
  const TOOLS = [
    { key: "revisions", label: <span className="inline-flex items-center gap-1"><History className="h-3 w-3" /> Revisions</span>, testId: "ops-frs-view-revisions" },
    { key: "diff", label: <span className="inline-flex items-center gap-1"><GitCompare className="h-3 w-3" /> Original → Current</span>, testId: "ops-frs-view-diff" },
    { key: "unmatched", label: <span className="inline-flex items-center gap-1"><Link2 className="h-3 w-3" /> Estimate links{c.unlinked ? ` (${c.unlinked})` : ""}</span>, testId: "ops-frs-view-unmatched" },
  ];

  return (
    <div className="space-y-3" data-testid="ops-frs-tab">
      {overview.review_required && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200" data-testid="ops-frs-review-banner">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>FRS requires review — {overview.review_reasons.map((r) => `${r.source}${r.note ? `: ${r.note}` : ""}`).join(" · ")}</span>
          {overview.can_edit && <Button size="sm" variant="nav" className="ml-auto text-xs" onClick={clearReview} data-testid="ops-frs-review-clear">Mark reviewed</Button>}
        </div>
      )}

      <RoleProvider>
        <div className="fo-scope rounded-lg" data-testid="ops-frs-scope">
          <FrsTable key={tick} projectId={project.id} opsMode />
        </div>
      </RoleProvider>

      <div className="flex flex-wrap items-center gap-2" data-testid="ops-frs-tools">
        <Segmented value={tool} onChange={(k) => setTool(k === tool ? "" : k)} options={TOOLS} />
        <span className="text-xs text-slate-500" data-testid="ops-frs-counts">
          {c.rooms} rooms · {c.items} items · {c.units} units · {c.scopes} scopes · {c.versions} version{c.versions === 1 ? "" : "s"}
        </span>
        {overview.can_edit && (
          <Button variant="data" size="sm" className="ml-auto text-xs" onClick={snapshot} disabled={busy} data-testid="ops-frs-snapshot">
            <Camera className="mr-1.5 h-3.5 w-3.5" /> Save version
          </Button>
        )}
      </div>
      {overview.post_open && overview.can_edit && tool && (
        <p className="text-xs text-slate-500">This job is Open — scope edits need a reason and should reference the change order.</p>
      )}
      {tool === "diff" && <FrsDiff project={project} overview={overview} />}
      {tool === "revisions" && <FrsRevisions project={project} overview={overview} tick={tick} />}
      {tool === "unmatched" && <FrsUnmatched project={project} canEdit={overview.can_edit} onChanged={changed} tick={tick} />}
    </div>
  );
}
